import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useLayoutEffect, useState, useEffect, useRef } from 'react';
import Player from '@vimeo/player';
import { IFrameContainer } from './IFrameContainer';
const PlayerAPI = _ref => {
  let {
    player,
    playing,
    muted = false,
    onEnd,
    onPlay,
    onPause,
    onProgress,
    disableSeekForward = false
  } = _ref;
  const previousTimeRef = useRef(0);
  const timeUpdateIntervalRef = useRef(null);
  useLayoutEffect(() => {
    player.setMuted(muted);
  }, [player, muted]);
  useLayoutEffect(() => {
    playing ? player.play() : player.pause();
  }, [player, playing]);
  useLayoutEffect(() => {
    // Store the current time periodically when disableSeekForward is enabled
    if (disableSeekForward) {
      const updatePreviousTime = async () => {
        try {
          const currentTime = await player.getCurrentTime();
          previousTimeRef.current = currentTime;
        } catch (error) {
          console.error('Error getting current time:', error);
        }
      };

      // Update previous time every second
      timeUpdateIntervalRef.current = setInterval(updatePreviousTime, 1000);

      // Listen for seek events
      const handleSeek = async event => {
        console.log('Vimeo: Seeking forward prevented, seeking back to:', event);
        try {
          const seekTargetTime = (event == null ? void 0 : event.seconds) || 0;

          // If seeking forward, seek back to previous position
          if (seekTargetTime > previousTimeRef.current) {
            console.log('Vimeo: Seeking forward prevented, seeking back to:', previousTimeRef.current);
            await player.setCurrentTime(previousTimeRef.current);
          }
        } catch (error) {
          console.error('Error handling seek:', error);
        }
      };
      player.on('seeking', handleSeek);

      // Clean up
      return () => {
        if (timeUpdateIntervalRef.current) {
          clearInterval(timeUpdateIntervalRef.current);
        }
        player.off('seeking', handleSeek);
      };
    }

    // Return undefined for the case when disableSeekForward is false
    return undefined;
  }, [player, disableSeekForward]);
  useLayoutEffect(() => {
    // Listen for Vimeo's own play events
    const handleVimeoPlay = () => {
      onPlay && onPlay();
    };
    player.on('playing', handleVimeoPlay);
    onPause && player.on('pause', onPause);
    onEnd && player.on('ended', onEnd);

    // Cleanup event listeners
    return () => {
      player.off('playing', handleVimeoPlay);
      onPause && player.off('pause', onPause);
      onEnd && player.off('ended', onEnd);
    };
  }, [player, onPlay, onPause, onEnd, onProgress]);
  return null;
};
export const Vimeo = props => {
  const {
    src,
    muted,
    onPlayerReady,
    disableSeekForward = false
  } = props;
  const [player, setPlayer] = useState();
  const [playing, setPlaying] = useState(props.playing || false);
  const setRef = useCallback(iframe => {
    if (iframe) {
      const vimeoPlayer = new Player(iframe, {
        muted,
        progress_bar: true,
        progressBar: true
      });
      onPlayerReady && onPlayerReady(vimeoPlayer);
      setPlayer(vimeoPlayer);
    }
  }, [muted]);

  // Update playing state when props.playing changes (for external control)
  useEffect(() => {
    if (props.playing !== undefined) {
      setPlaying(props.playing);
    }
  }, [props.playing]);
  const handlePlay = useCallback(() => {
    // Update state when video plays externally
    if (!playing) {
      setPlaying(true);
    }
    // Call the original onPlay callback if provided
    props.onPlay && props.onPlay();
  }, [playing, props.onPlay]);
  return /*#__PURE__*/React.createElement(IFrameContainer, {
    poster: props.poster,
    playing: playing
  }, size => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("iframe", {
      ref: setRef,
      title: "Vimeo",
      src: src,
      frameBorder: "0",
      allowFullScreen: true,
      allow: "autoplay; encrypted-media",
      width: size.width,
      height: size.height
    }), player ? /*#__PURE__*/React.createElement(PlayerAPI, _extends({}, props, {
      playing: playing,
      onPlay: handlePlay,
      player: player,
      disableSeekForward: disableSeekForward
    })) : null);
  });
};
//# sourceMappingURL=Vimeo.js.map